import { hostOf, NETWORK_ERROR, RATE_LIMITED } from "../services/HttpError";

/**
 * Turns a sign-in failure into something the login screen can act on.
 *
 * The API's auth endpoints reject with `{ code, message, details, request_id }`
 * (see backend `src/auth/AuthError.ts`); HttpService surfaces that as an
 * HttpError, and adds its own codes for failures that never reached the API
 * (NETWORK_ERROR, WORKSPACE_LOOKUP_FAILED, WORKSPACE_NOT_FOUND). This table
 * maps each code to a title, a fallback message, which form field to flag,
 * and an optional next step. The server's own `message` wins when present -
 * it is written for the user and is more specific than the fallback.
 */
const FORGOT = { label: "Reset your password", to: "/forgot-password" };
const BACK_TO_LOGIN = { label: "Back to sign in", to: "/login" };

// `message` in an entry overrides the server's text - only set where the
// server body has no user-facing copy (rate limiter) or the failure never
// reached the server.
const CATALOG = {
    WORKSPACE_REQUIRED: { title: "Workspace missing", field: "workspace" },
    USERNAME_REQUIRED: { title: "Email missing", field: "username" },
    PASSWORD_REQUIRED: { title: "Password missing", field: "password" },
    WORKSPACE_NOT_FOUND: { title: "Workspace not found", field: "workspace" },
    WORKSPACE_LOOKUP_FAILED: {
        title: "Can't reach the workspace directory",
        transient: true,
    },
    WORKSPACE_UNAVAILABLE: { title: "Workspace unavailable", transient: true },
    [NETWORK_ERROR]: {
        title: "Can't reach the server",
        transient: true,
        message: (error) =>
            `Could not reach ${hostOf(
                error?.url,
            )}. Check your connection - or the server may be down.`,
    },
    [RATE_LIMITED]: {
        title: "Too many attempts",
        message:
            "Sign-in is temporarily locked for this network after repeated attempts. Wait a few minutes and try again.",
    },
    INVALID_CREDENTIALS: {
        title: "Sign in failed",
        field: "password",
        action: FORGOT,
    },
    PASSWORD_NOT_SET: {
        title: "No password on this account",
        action: { label: "Set a password", to: "/forgot-password" },
    },
    ACCOUNT_INACTIVE: { title: "Account deactivated" },
    ACCOUNT_ARCHIVED: { title: "Account removed" },
    // Signed in fine, but not a member of this workspace (or suspended in it).
    NOT_A_MEMBER: { title: "No access to this workspace", action: BACK_TO_LOGIN },
    SSO_CODE_MISSING: { title: "Starek sign-in failed", action: BACK_TO_LOGIN },
    SSO_CODE_EXCHANGE_FAILED: {
        title: "Starek sign-in failed",
        action: BACK_TO_LOGIN,
    },
    SSO_TOKEN_INVALID: { title: "Starek sign-in failed", action: BACK_TO_LOGIN },
    SSO_PROVIDER_UNREACHABLE: {
        title: "Sign-in provider unreachable",
        transient: true,
    },
    // Set by SSOLogin when the provider bounced back with ?error= instead of
    // a code - the failure happened before our API was involved.
    SSO_PROVIDER_REJECTED: {
        title: "Starek sign-in was declined",
        action: BACK_TO_LOGIN,
    },
    SSO_STATE_MISSING: {
        title: "Starek sign-in failed",
        message:
            "The sign-in callback was incomplete. Start the sign-in again from the login page.",
        action: BACK_TO_LOGIN,
    },
    INTERNAL_ERROR: { title: "Server error" },
};

const GENERIC_MESSAGE =
    "Sign in failed. Please check your workspace, email and password, then try again.";

export function describeSignInError(error) {
    const code = error?.code || null;
    const entry = CATALOG[code] || {};
    const override =
        typeof entry.message === "function" ? entry.message(error) : entry.message;
    const message = override || error?.message || GENERIC_MESSAGE;

    return {
        code,
        status: error?.status ?? null,
        requestId: error?.requestId || null,
        url: error?.url || null,
        details: error?.details || null,
        title: entry.title || "Sign in failed",
        message,
        field: entry.field || null,
        action: entry.action || null,
        transient: !!entry.transient,
        at: new Date(),
    };
}

/** One line for a support ticket / clipboard. */
export function signInErrorSummary(described) {
    const parts = [
        described.code || "UNKNOWN",
        described.status != null ? `HTTP ${described.status}` : null,
        described.requestId ? `ref ${described.requestId}` : null,
        described.url ? hostOf(described.url) : null,
        described.at?.toISOString?.(),
    ].filter(Boolean);
    return parts.join(" · ");
}
